import React from 'react';
import { Check } from 'lucide-react';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';

export interface ProgressSection {
  code: string;
  name: string;
  description?: string;
}

export interface ProgressBannerProps {
  currentScreenCode: string;
  claimType: 'MEDICAL' | 'CANCELLATION' | 'BAGGAGE';
  completedScreens: string[];
}

// Define workflow steps for each claim type (sections 01-05 only)
//
// DUPLICATED on purpose (historically) with `utils/flowScreens.getWorkflowSteps`.
// Exported — hence the react-refresh exemption — so a test can assert the two copies
// are identical; when they drift the app reports two different step totals.
// eslint-disable-next-line react-refresh/only-export-components
export const getWorkflowSteps = (type: 'MEDICAL' | 'CANCELLATION' | 'BAGGAGE'): string[] => {
  const baseSteps = [
    // Section 01: Applicant Information (4 steps)
    '01-00-DEMANDEUR',
    '01-01-CONTRAT',
    '01-02-AUTRES-CONTRATS',
    // Conditional (DEV-7479) but ALWAYS counted in the total — same convention as
    // '05M-02-MANDAT' and the five mutually exclusive '03A-01-DETAILS*' screens.
    '01-03-PAIEMENT-VOYAGE',
    // Section 02: Coverage (3 steps)
    '02-00-GARANTIE',
  ];

  if (type === 'MEDICAL') {
    return [
      ...baseSteps,
      '02M-01-BENEFICIAIRE',
      '02M-02-VOYAGE',
      // Section 03: Event Details (3 steps)
      '03M-00-ACCIDENT',
      '03M-01-DETAILS',
      '03M-02-VERIFICATIONS',
      // Section 04: Reimbursement (4 steps)
      '04M-00-REMBOURSEMENT',
      '04-01-FACTURES',
      '04-02-VALIDATION',
      '04-03-JUSTIFICATIFS',
      // Section 05: Payment & Submission (3 steps)
      '05-01-PAIEMENT',
      '05M-02-MANDAT',
      '05-03-FIN'
    ]; // Total: 17 steps
  } else if (type === 'CANCELLATION') {
    return [
      ...baseSteps,
      '02A-01-BENEFICIAIRE',
      '02A-02-VOYAGE',
      // Section 03: Event Details (6 steps - multiple detail screens)
      '03A-01-DETAILS1',
      '03A-01-DETAILS2',
      '03A-01-DETAILS3',
      '03A-01-DETAILS4',
      '03A-01-DETAILS5',
      '03A-02-VERIFICATIONS',
      // Section 04: Documents (3 steps - no reimbursement)
      '04-01-FACTURES',
      '04-02-VALIDATION',
      '04-03-JUSTIFICATIFS',
      // Section 05: Payment & Submission (2 steps - no mandate)
      '05-01-PAIEMENT',
      '05-03-FIN'
    ]; // Total: 18 steps
  } else { // BAGGAGE
    return [
      ...baseSteps,
      '02B-01-BENEFICIAIRE',
      '02B-02-VOYAGE',
      // Section 03: Event Details (2 steps - no accident verification)
      '03B-01-DETAILS',
      '03B-02-VERIFICATIONS',
      // Section 04: Reimbursement (4 steps)
      '04B-00-REMBOURSEMENT',
      '04-01-FACTURES',
      '04-02-VALIDATION',
      '04-03-JUSTIFICATIFS',
      // Section 05: Payment & Submission (2 steps - no mandate)
      '05-01-PAIEMENT',
      '05-03-FIN'
    ]; // Total: 15 steps
  }
};

const ProgressBanner: React.FC<ProgressBannerProps> = ({
  currentScreenCode,
  claimType,
  completedScreens = []
}) => {
  const { t } = useTranslation();

  // Detect claim type from screen code if not provided correctly
  const detectClaimTypeFromScreenCode = (screenCode: string): 'MEDICAL' | 'CANCELLATION' | 'BAGGAGE' => {
    if (screenCode.includes('M-')) return 'MEDICAL';
    if (screenCode.includes('A-')) return 'CANCELLATION';
    if (screenCode.includes('B-')) return 'BAGGAGE';
    return claimType; // fallback to provided claim type
  };

  const actualClaimType = detectClaimTypeFromScreenCode(currentScreenCode);

  // Define the 5 major sections for claim submission (01-05 only, excluding 00)
  const sections: ProgressSection[] = [
    {
      code: '01',
      name: t('progress.banner.sections.applicant.title', 'Applicant'),
      description: t('progress.banner.sections.applicant.description', 'Your Information')
    },
    {
      code: '02',
      name: t('progress.banner.sections.coverage.title', 'Coverage'),
      description: t('progress.banner.sections.coverage.description', 'Benefit Selection')
    },
    {
      code: '03',
      name: t('progress.banner.sections.event.title', 'Event'),
      description: t('progress.banner.sections.event.description', 'Event Details')
    },
    {
      code: '04',
      name: t('progress.banner.sections.documents.title', 'Documents'),
      description: t('progress.banner.sections.documents.description', 'Upload & Validation')
    },
    {
      code: '05',
      name: t('progress.banner.sections.submission.title', 'Submission'),
      description: t('progress.banner.sections.submission.description', 'Payment & Finalization')
    }
  ];

  const workflowSteps = getWorkflowSteps(actualClaimType);
  const currentStepIndex = workflowSteps.findIndex(step => step === currentScreenCode);
  const totalSteps = workflowSteps.length;

  // Extract section code from screen code (e.g., "03M-01-DETAILS" -> "03")
  const getCurrentSectionCode = (): string => {
    if (!currentScreenCode) return '00';
    return currentScreenCode.substring(0, 2);
  };

  const currentSectionCode = getCurrentSectionCode();
  const currentSectionIndex = sections.findIndex(s => s.code === currentSectionCode);

  // Calculate overall progress percentage based on actual steps
  const calculateProgress = (): number => {
    if (currentSectionCode === '00') return 0;
    if (currentStepIndex === -1) return 0;
    if (currentScreenCode.includes('FIN')) return 100;

    // Calculate based on current step position in workflow
    const stepProgress = ((currentStepIndex + 1) / totalSteps) * 100;
    return Math.min(Math.max(stepProgress, 0), 100);
  };

  const progressPercentage = calculateProgress();
  const currentStepNumber = currentStepIndex >= 0 ? currentStepIndex + 1 : 1;

  // Determine section status
  const getSectionStatus = (sectionCode: string): 'completed' | 'current' | 'upcoming' => {
    // If we're in section 00 (home), all sections are upcoming
    if (currentSectionCode === '00') return 'upcoming';

    const sectionIndex = sections.findIndex(s => s.code === sectionCode);
    if (sectionIndex < currentSectionIndex) return 'completed';
    if (sectionCode === currentSectionCode) return 'current';
    return 'upcoming';
  };

  // Get readable screen identifier
  const getScreenName = (): string => {
    // Map common screen codes to readable names
    const screenNames: Record<string, string> = {
      '00-00-ACCUEIL': t('progress.banner.screens.home', 'Home'),
      '00-01-NOUVELLE': t('progress.banner.screens.newRequest', 'New Request'),
      '00-02-AVIS': t('progress.banner.screens.consent', 'Automated Consent'),
      '01-00-DEMANDEUR': t('progress.banner.screens.contactPreferences', 'Contact Preferences'),
      '01-01-CONTRAT': t('progress.banner.screens.contract', 'Contract Selection'),
      '01-02-AUTRES-CONTRATS': t('progress.banner.screens.otherInsurance', 'Other Insurance'),
      '01-03-PAIEMENT-VOYAGE': t('progress.banner.screens.tripPaymentMethod', 'Trip Payment Method'),
      '02-00-GARANTIE': t('progress.banner.screens.coverage', 'Coverage Selection'),
      '02M-01-BENEFICIAIRE': t('progress.banner.screens.beneficiary', 'Beneficiary'),
      '02M-02-VOYAGE': t('progress.banner.screens.travel', 'Travel Details'),
      '02A-01-BENEFICIAIRE': t('progress.banner.screens.beneficiary', 'Beneficiary'),
      '02A-02-VOYAGE': t('progress.banner.screens.travel', 'Travel Details'),
      '02B-01-BENEFICIAIRE': t('progress.banner.screens.beneficiary', 'Beneficiary'),
      '02B-02-VOYAGE': t('progress.banner.screens.travel', 'Travel Details'),
      '03M-00-ACCIDENT': t('progress.banner.screens.accident', 'Accident Verification'),
      '03M-01-DETAILS': t('progress.banner.screens.eventDetails', 'Event Details'),
      '03M-02-VERIFICATIONS': t('progress.banner.screens.verifications', 'Medical Verifications'),
      '04M-00-REMBOURSEMENT': t('progress.banner.screens.reimbursement', 'Reimbursement Details'),
      '04-01-FACTURES': t('progress.banner.screens.invoices', 'Invoice Upload'),
      '04-02-VALIDATION': t('progress.banner.screens.validation', 'Expense Validation'),
      '04-03-JUSTIFICATIFS': t('progress.banner.screens.supportingDocs', 'Supporting Documents'),
      '05-01-PAIEMENT': t('progress.banner.screens.payment', 'Payment Information'),
      '05M-02-MANDAT': t('progress.banner.screens.mandate', 'Mandate Signature'),
      '05-03-FIN': t('progress.banner.screens.confirmation', 'Confirmation')
    };

    return screenNames[currentScreenCode] || currentScreenCode;
  };

  return (
    <div className="bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        {/* Progress Bar */}
        <div className="mb-3">
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-tenant-primary h-2.5 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progressPercentage}%` }}
              role="progressbar"
              aria-valuenow={progressPercentage}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={t('progress.banner.progressLabel', {
                defaultValue: 'Step {{current}} of {{total}}: {{percent}}% complete',
                current: currentStepNumber,
                total: totalSteps,
                percent: Math.round(progressPercentage)
              })}
            />
          </div>
        </div>

        {/* Section Markers */}
        <div className="flex items-center justify-between gap-1">
          {sections.map((section, index) => {
            const status = getSectionStatus(section.code);
            const isCompleted = status === 'completed';
            const isCurrent = status === 'current';
            const isUpcoming = status === 'upcoming';

            return (
              <div
                key={section.code}
                className="flex-1 flex flex-col items-center relative"
              >
                {/* Section Marker Circle */}
                <div
                  className={clsx(
                    'w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold transition-all duration-300',
                    {
                      'bg-green-500 text-white shadow-md': isCompleted,
                      'bg-tenant-primary text-white shadow-lg ring-4 ring-tenant-100 scale-110': isCurrent,
                      'bg-gray-200 text-gray-500': isUpcoming
                    }
                  )}
                  aria-label={`${section.name}: ${status}`}
                >
                  {isCompleted ? (
                    <Check className="w-4 h-4 sm:w-5 sm:h-5" aria-hidden="true" />
                  ) : (
                    <span>{section.code}</span>
                  )}
                </div>

                {/* Section Label (Hidden on Mobile) */}
                <div className="hidden sm:block mt-1 text-center">
                  <div
                    className={clsx(
                      'text-xs font-medium transition-colors duration-300',
                      {
                        'text-green-600': isCompleted,
                        'text-tenant-primary': isCurrent,
                        'text-gray-500': isUpcoming
                      }
                    )}
                  >
                    {section.name}
                  </div>
                  {section.description && (
                    <div className="text-xs text-gray-400 mt-0.5">
                      {section.description}
                    </div>
                  )}
                </div>

                {/* Mobile: Show current section name below markers */}
                {isCurrent && (
                  <div className="sm:hidden mt-1 text-center">
                    <div className="text-xs font-medium text-tenant-primary">
                      {section.name}
                    </div>
                  </div>
                )}

                {/* Connector Line (except for last section) */}
                {index < sections.length - 1 && (
                  <div
                    className={clsx(
                      'absolute h-0.5 top-4 sm:top-5 transition-colors duration-300',
                      {
                        'bg-green-500': isCompleted,
                        'bg-gray-200': !isCompleted
                      }
                    )}
                    style={{
                      left: '50%',
                      right: '-50%',
                      zIndex: -1
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ProgressBanner;
